import React, { useEffect, useRef, useState } from 'react';
import { CLASSIFICATION_OPTIONS, VERIFICATION_OPTIONS } from '../lib/filters';
import { downloadCsv } from '../lib/storage';

function summarize(selectedIds, options, allLabel = 'all') {
  if (!selectedIds.length) return 'none';
  if (selectedIds.length === options.length) return allLabel;
  const labels = options
    .filter((o) => selectedIds.includes(o.id))
    .map((o) => o.label);
  if (labels.length <= 2) return labels.join(', ');
  return `${labels.length} types`;
}

function CheckboxDropdown({ summary, options, selected, onChange, align = 'left' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const allSelected = options.length > 0 && options.every((o) => selected.includes(o.id));

  const toggle = (id) => {
    onChange(
      selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
    );
  };

  return (
    <span className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-paper-line bg-white hover:bg-wash text-[13px] text-ink font-medium underline decoration-paper-line underline-offset-2"
      >
        {summary}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3 text-ink-faint">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div
          className={`absolute top-full mt-1.5 z-50 min-w-[200px] max-h-[280px] overflow-y-auto bg-white border border-paper-line rounded-md shadow-popover py-1 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <label className="flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-wash cursor-pointer border-b border-paper-line">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={(e) =>
                onChange(e.target.checked ? options.map((o) => o.id) : [])
              }
              className="rounded border-paper-line"
            />
            <span className="font-medium">Select all</span>
          </label>
          {options.map((opt) => (
            <label
              key={opt.id}
              className="flex items-center gap-2 px-3 py-1.5 text-[12px] hover:bg-wash cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.id)}
                onChange={() => toggle(opt.id)}
                className="rounded border-paper-line"
              />
              <span>{opt.label}</span>
              <span className="ml-auto text-[10px] text-ink-faint truncate max-w-[90px]">
                {opt.id !== opt.label ? opt.id : ''}
              </span>
            </label>
          ))}
        </div>
      )}
    </span>
  );
}

function ExportEmailsModal({
  open,
  onClose,
  emailFilters,
  onEmailFiltersChange,
  filteredEmails = [],
}) {
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const verifications = emailFilters?.verifications || [];
  const classifications = emailFilters?.classifications || [];
  const setVerifications = (next) =>
    onEmailFiltersChange({
      ...emailFilters,
      verifications: typeof next === 'function' ? next(verifications) : next,
    });
  const setClassifications = (next) =>
    onEmailFiltersChange({
      ...emailFilters,
      classifications: typeof next === 'function' ? next(classifications) : next,
    });

  const handleDownload = () => {
    downloadCsv(
      `emails-${new Date().toISOString().slice(0, 10)}.csv`,
      ['email', 'domain', 'classification', 'verification', 'score', 'savedAt', 'launchedAt'],
      filteredEmails.map((e) => [
        e.email,
        e.domain,
        e.classification || '',
        e.verification || '',
        e.score ?? '',
        e.savedAt || '',
        e.launchedAt || '',
      ])
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/20" onClick={onClose} />
      <div
        ref={dialogRef}
        role="dialog"
        aria-labelledby="export-emails-title"
        className="relative bg-white rounded-md shadow-popover border border-paper-line w-full max-w-md overflow-hidden"
      >
        <div className="px-5 pt-4 pb-3 border-b border-paper-line flex items-start justify-between gap-3">
          <div>
            <h2 id="export-emails-title" className="text-[15px] font-semibold tracking-tight text-ink">
              Export contacts
            </h2>
            <p className="text-[12px] text-ink-faint mt-0.5">
              Filters apply to Contacts and to Emails.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-faint hover:text-ink p-1"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-ink">
            <span>Export</span>
            <CheckboxDropdown
              summary={summarize(verifications, VERIFICATION_OPTIONS, 'all')}
              options={VERIFICATION_OPTIONS}
              selected={verifications}
              onChange={setVerifications}
            />
            <span>contacts that are</span>
            <CheckboxDropdown
              summary={summarize(classifications, CLASSIFICATION_OPTIONS, 'any type')}
              options={CLASSIFICATION_OPTIONS}
              selected={classifications}
              onChange={setClassifications}
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-paper-line flex items-center justify-end gap-2 bg-paper-soft">
          <button
            type="button"
            onClick={onClose}
            className="text-[13px] px-3 py-1.5 rounded-[4px] border border-paper-strong bg-white hover:bg-wash"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={filteredEmails.length === 0}
            className="text-[13px] px-3 py-1.5 rounded-[4px] bg-cta text-white hover:bg-cta-hover disabled:opacity-40"
          >
            Download CSV
            <span className="ml-1.5 text-[12px] text-white/80 tabular-nums">
              {filteredEmails.length}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExportEmailsModal;
